import { ModalBareboneProps, styled } from '@zeiss/beyond-online-react';
import {
  AlertText,
  GeneralModal,
  ModalText,
  StyledContainer,
} from '@zeiss/lib-components';
import { useBreakpoint } from '@zeiss/lib-helpers';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { showToast } from 'src/helpers/showToast';
import { useDeleteTenant } from 'src/hooks/api';

const ModalTextContent = styled.div<StyledContainer>`
  & > p:first-of-type {
    margin: 0;
  }
`;

interface DeleteTenantModalProps extends Pick<
  ModalBareboneProps,
  'isOpen' | 'onClose'
> {
  applicationListId?: string;
  data: { id: string; name: string };
}

export const DeleteTenantModal = ({
  applicationListId,
  data: { id, name },
  isOpen,
  onClose,
}: DeleteTenantModalProps) => {
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const { isPending, mutate: deleteTenant } = useDeleteTenant(id);

  const isMinTablet = useBreakpoint('tablet');

  const removeTenant = () => {
    deleteTenant(undefined, {
      onError: () => {
        showToast({
          props: {
            title: t('common:notification.errorX', {
              action: t('common:action.deleted'),
              x: t('common:glossary.tenant'),
            }),
            type: 'error',
          },
        });
      },
      onSuccess: () => {
        showToast({
          props: {
            title: t('common:notification.successX', {
              action: t('common:action.deleted'),
              x: t('common:glossary.tenant'),
            }),
            type: 'success',
          },
        });
        onClose?.();
        navigate(`/tenant-management/${applicationListId}/tenants`);
      },
    });
  };

  return (
    <GeneralModal
      acceptButtonText={t('common:action.delete')}
      declineButtonText={t('common:action.cancel')}
      headline={t('common:action.deleteX', {
        x: t('common:glossary.tenant'),
      })}
      isOpen={isOpen}
      loading={isPending}
      onAccept={removeTenant}
      onClose={onClose}
      onDecline={onClose}
      size="m"
    >
      <ModalTextContent $isMinTablet={isMinTablet}>
        <ModalText>
          <Trans i18nKey="common:text.deleteTenantX" t={t} values={{ name }}>
            <b />
          </Trans>
        </ModalText>

        <AlertText
          icon="StatusActionRequired"
          text={t('common:text.deleteTenantWarning')}
        />
      </ModalTextContent>
    </GeneralModal>
  );
};
